import React from 'react';
import { Search } from 'lucide-react';
import AppSelect from 'src/components/common/AppSelect';
import { cn } from 'src/lib/utils';

const OPPORTUNITY_TYPE_OPTIONS = [
  { label: 'All', value: 'ALL' },
  { label: 'Internal Opportunities', value: 'INTERNAL' },
  { label: 'Customer Opportunities', value: 'CUSTOMER' },
];

// Fixed per-tab dot colour — not selection-dependent, so a tab stays
// recognisable at a glance even when it isn't the active one.
const TABS = [
  { key: 'active', label: 'Active', dotClass: 'bg-primary' },
  { key: 'closed', label: 'Closed', dotClass: 'bg-ds-success' },
  { key: 'archive', label: 'Archive', dotClass: 'bg-muted-foreground' },
];

const TaFilterBar = ({
  searchQuery,
  onSearchChange,
  activeTab,
  onTabChange,
  mockFilter = 'ALL',
  onMockFilterChange,
  searchPlaceholder = 'Search role, Reg ID...',
  // Optional { [tabKey]: count } — same count-badge treatment the CSE
  // Opportunities tabs use. Keyed by tab key (active/closed/archive), not
  // whatever field name the API's own metrics object uses for each, so
  // this component doesn't need to know about that mapping — the caller
  // (TaAssignmentView) resolves it first. A tab with no entry (or a
  // response that doesn't have `metrics` yet at all) just shows no badge,
  // same `!= null` guard CSE's own tab bar uses.
  tabCounts,
}) => (
  <div className="flex justify-between items-center gap-3 mb-5">
    <div className="flex items-center gap-4">
      {/* Status Tabs — dot-per-tab underline style (§5.13) */}
      <div className="flex items-center gap-1 border-b border-border">
        {TABS.map(({ key, label, dotClass }) => {
          const isActive = activeTab === key;
          const count = tabCounts?.[key];
          return (
            <button
              key={key}
              type="button"
              onClick={() => onTabChange(key)}
              className={cn(
                'inline-flex items-center gap-[7px] px-[14px] py-[10px] -mb-px text-[13px] font-[550] bg-transparent border-0 border-b-2 cursor-pointer transition-colors whitespace-nowrap',
                isActive
                  ? 'text-primary border-primary'
                  : 'text-muted-foreground border-transparent hover:text-foreground-strong'
              )}
            >
              <span
                className={cn(
                  'w-[7px] h-[7px] rounded-full flex-shrink-0',
                  dotClass
                )}
              />
              {label}
              {count != null && (
                <span
                  className={cn(
                    'text-[11px] font-semibold px-[7px] py-[1px] rounded-full',
                    isActive
                      ? 'bg-brand-bg-subtle text-primary'
                      : 'bg-muted text-muted-foreground'
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {activeTab !== 'archive' && (
        <AppSelect
          options={OPPORTUNITY_TYPE_OPTIONS}
          value={mockFilter}
          onValueChange={onMockFilterChange}
          placeholder="All"
          className="h-9 w-[200px] text-[12.5px] font-medium"
        />
      )}
    </div>

    {/* Search Input — design system filter search (§5.7) */}
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-[15px] h-[15px] text-muted-foreground pointer-events-none" />
      <input
        type="text"
        placeholder={searchPlaceholder}
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        className="pl-[34px] pr-3 py-2 text-[12.5px] text-foreground-strong border border-input rounded-lg w-[260px] outline-none bg-card placeholder:text-muted-foreground focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08] transition-[border-color,box-shadow]"
      />
    </div>
  </div>
);

export default TaFilterBar;
